/**
 * Admin Page Editor (spec 080).
 *
 * Route: /admin/pages
 *
 * Allows a chapter admin to:
 *   - Pick a scope (institution/chapter/branch) via dropdowns.
 *   - See the list of pages in that scope.
 *   - Create a new page (slug + title; optional landing/public flags).
 *   - Select a page and edit its parts: add HTML/Gallery parts, reorder
 *     with up/down buttons, edit HTML via RichTextEditor, configure gallery
 *     filter, save the whole part-array atomically via PUT /api/pages/:id/parts.
 *   - Preview the rendered page below the editor (DOMPurify on render).
 *
 * Drag-and-drop reordering is intentionally limited to up/down buttons in
 * v1 — HTML5 DnD adds a lot of UI surface that doesn't change behaviour.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiClientError,
  chaptersApi,
  pagesApi,
  type BranchSummary,
  type ChapterSummary,
  type GalleryPartConfig,
  type HtmlPartConfig,
  type PageDetail,
  type PagePartConfig,
  type PagePartKind,
  type PageSummary,
  type ScopeType,
} from '@/services/api';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import { PageRenderer } from '@/components/pages/PageRenderer';

interface DraftPart {
  id: string; // local id; backend assigns its own on save
  kind: PagePartKind;
  config: PagePartConfig;
}

let draftPartCounter = 0;
function makeDraftId() {
  draftPartCounter += 1;
  return `draft-${draftPartCounter}`;
}

export function PageEditor() {
  // Scope selection state
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [scopeType, setScopeType] = useState<ScopeType>('chapter');
  const [chapterId, setChapterId] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');

  // Pages list + selection
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [pageDetail, setPageDetail] = useState<PageDetail | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  // New page form
  const [showCreate, setShowCreate] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newIsLanding, setNewIsLanding] = useState(false);
  const [newIsPublic, setNewIsPublic] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Working part list
  const [drafts, setDrafts] = useState<DraftPart[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const selectedScopeId = useMemo(() => {
    if (scopeType === 'branch') return branchId;
    return chapterId;
  }, [scopeType, chapterId, branchId]);

  // Load chapters once.
  useEffect(() => {
    chaptersApi
      .list('navarasam')
      .then((rows) => {
        setChapters(rows);
        if (rows.length > 0 && !chapterId) setChapterId(rows[0]!.id);
      })
      .catch(() => setChapters([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load branches when chapter changes.
  useEffect(() => {
    if (!chapterId) {
      setBranches([]);
      return;
    }
    chaptersApi
      .listBranches(chapterId)
      .then((rows) => {
        setBranches(rows);
        if (scopeType === 'branch' && rows.length > 0 && !branchId) setBranchId(rows[0]!.id);
      })
      .catch(() => setBranches([]));
  }, [chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load pages when scope changes.
  const reloadPages = useCallback(async () => {
    if (!selectedScopeId) {
      setPages([]);
      return;
    }
    try {
      const rows = await pagesApi.list(scopeType, selectedScopeId);
      setPages(rows);
      setPagesError(null);
    } catch (err) {
      setPages([]);
      setPagesError(err instanceof Error ? err.message : 'Failed to load pages');
    }
  }, [scopeType, selectedScopeId]);

  useEffect(() => {
    void reloadPages();
  }, [reloadPages]);

  // Load page detail when selectedPageId changes.
  useEffect(() => {
    if (!selectedPageId) {
      setPageDetail(null);
      setDrafts([]);
      return;
    }
    setPageLoading(true);
    setPageError(null);
    pagesApi
      .getById(selectedPageId)
      .then((p) => {
        setPageDetail(p);
        setDrafts(
          [...p.parts]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((part) => ({ id: makeDraftId(), kind: part.kind, config: part.config })),
        );
      })
      .catch((err: Error) => setPageError(err.message))
      .finally(() => setPageLoading(false));
  }, [selectedPageId]);

  // ---------- Mutations ----------

  async function handleCreatePage(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const created = await pagesApi.create({
        scopeType,
        scopeId: selectedScopeId,
        slug: newSlug,
        title: newTitle,
        isLandingPage: newIsLanding,
        isPublic: newIsPublic,
      });
      setShowCreate(false);
      setNewSlug('');
      setNewTitle('');
      setNewIsLanding(false);
      setNewIsPublic(true);
      await reloadPages();
      setSelectedPageId(created.id);
    } catch (err) {
      setCreateError(err instanceof ApiClientError ? err.message : 'Failed to create page');
    } finally {
      setCreating(false);
    }
  }

  function addHtmlPart() {
    setDrafts((prev) => [
      ...prev,
      { id: makeDraftId(), kind: 'html', config: { html: '<p>New section</p>', container: 'wide' } },
    ]);
  }

  function addGalleryPart() {
    setDrafts((prev) => [
      ...prev,
      { id: makeDraftId(), kind: 'gallery', config: { filter: {}, layout: 'grid' } },
    ]);
  }

  function move(index: number, delta: -1 | 1) {
    setDrafts((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  function removeAt(index: number) {
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  }

  function updateAt(index: number, config: PagePartConfig) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, config } : d)));
  }

  async function handleSave() {
    if (!pageDetail) return;
    setSaving(true);
    setSaveError(null);
    setSaveMessage(null);
    try {
      const next = await pagesApi.replaceParts(
        pageDetail.id,
        drafts.map((d, i) => ({ kind: d.kind, config: d.config, sortOrder: i })),
      );
      setPageDetail(next);
      setSaveMessage('Saved.');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 2500);
    }
  }

  async function handleDeletePage() {
    if (!pageDetail) return;
    if (!confirm(`Delete page "${pageDetail.title}"? This cannot be undone.`)) return;
    try {
      await pagesApi.remove(pageDetail.id);
      setSelectedPageId(null);
      setPageDetail(null);
      await reloadPages();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  // ---------- UI ----------

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold text-white">Pages</h1>
        <p className="text-white/60 mt-1">Build landing pages and custom pages with HTML and Gallery parts.</p>
      </div>

      {/* Scope picker */}
      <section className="card">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Scope type</label>
            <select
              value={scopeType}
              onChange={(e) => {
                setScopeType(e.target.value as ScopeType);
                setSelectedPageId(null);
              }}
              className="input"
            >
              <option value="chapter">Chapter</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Chapter</label>
            <select
              value={chapterId}
              onChange={(e) => {
                setChapterId(e.target.value);
                setSelectedPageId(null);
              }}
              className="input"
            >
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </div>
          {scopeType === 'branch' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Branch</label>
              <select
                value={branchId}
                onChange={(e) => {
                  setBranchId(e.target.value);
                  setSelectedPageId(null);
                }}
                className="input"
                disabled={branches.length === 0}
              >
                {branches.length === 0 ? (
                  <option value="">— No branches —</option>
                ) : (
                  branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.displayName ?? b.name}</option>
                  ))
                )}
              </select>
            </div>
          )}
        </div>
      </section>

      {/* Page list + create */}
      <section className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Pages in this scope</h2>
          <button type="button" onClick={() => setShowCreate((v) => !v)} className="btn-secondary text-sm px-3 py-1.5">
            {showCreate ? 'Cancel' : '+ New page'}
          </button>
        </div>

        {showCreate && (
          <form onSubmit={handleCreatePage} className="mb-6 space-y-3 p-4 rounded bg-white/5 border border-white/10">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-white/80 mb-1">Slug</label>
                <input
                  type="text"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value.toLowerCase())}
                  pattern="[a-z0-9][a-z0-9-]{0,38}[a-z0-9]?"
                  required
                  className="input"
                  placeholder="landing"
                />
              </div>
              <div>
                <label className="block text-sm text-white/80 mb-1">Title</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  required
                  className="input"
                />
              </div>
            </div>
            <div className="flex gap-4 text-sm text-white/80">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newIsLanding} onChange={(e) => setNewIsLanding(e.target.checked)} />
                Landing page (only one per scope)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newIsPublic} onChange={(e) => setNewIsPublic(e.target.checked)} />
                Public (visible without login)
              </label>
            </div>
            {createError && <p className="text-red-400 text-sm">{createError}</p>}
            <button type="submit" disabled={creating} className="btn-primary">
              {creating ? 'Creating…' : 'Create page'}
            </button>
          </form>
        )}

        {pagesError ? (
          <p className="text-red-400">{pagesError}</p>
        ) : pages.length === 0 ? (
          <p className="text-white/60">No pages in this scope yet.</p>
        ) : (
          <ul className="divide-y divide-white/10">
            {pages.map((p) => (
              <li
                key={p.id}
                onClick={() => setSelectedPageId(p.id)}
                className={`py-2 px-2 cursor-pointer flex items-center justify-between rounded ${
                  selectedPageId === p.id ? 'bg-primary/10' : 'hover:bg-white/5'
                }`}
              >
                <div>
                  <p className="text-white font-medium">{p.title}</p>
                  <p className="text-xs text-white/40">/{p.slug}{p.isLandingPage ? ' · landing' : ''}{p.isPublic ? ' · public' : ' · private'}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Editor */}
      {selectedPageId && (
        <section className="card">
          {pageLoading ? (
            <p className="text-white/60">Loading page…</p>
          ) : pageError ? (
            <p className="text-red-400">{pageError}</p>
          ) : pageDetail ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-white">{pageDetail.title}</h2>
                  <p className="text-xs text-white/40">/{pageDetail.slug}</p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={addHtmlPart} className="btn-secondary text-sm px-3 py-1.5">+ HTML part</button>
                  <button type="button" onClick={addGalleryPart} className="btn-secondary text-sm px-3 py-1.5">+ Gallery part</button>
                  <button type="button" onClick={handleDeletePage} className="text-xs text-red-400 hover:text-red-300 px-3 py-1.5">Delete page</button>
                </div>
              </div>

              <div className="space-y-4">
                {drafts.length === 0 ? (
                  <p className="text-white/60 text-sm">No parts yet. Add an HTML or Gallery part above.</p>
                ) : (
                  drafts.map((d, idx) => (
                    <PartEditor
                      key={d.id}
                      index={idx}
                      total={drafts.length}
                      part={d}
                      pageId={pageDetail.id}
                      onMove={(delta) => move(idx, delta)}
                      onRemove={() => removeAt(idx)}
                      onChange={(cfg) => updateAt(idx, cfg)}
                    />
                  ))
                )}
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button type="button" onClick={handleSave} disabled={saving} className="btn-primary">
                  {saving ? 'Saving…' : 'Save parts'}
                </button>
                {saveMessage && <span className="text-green-400 text-sm">{saveMessage}</span>}
                {saveError && <span className="text-red-400 text-sm">{saveError}</span>}
              </div>

              {/* Preview */}
              <div className="mt-8 border-t border-white/10 pt-6">
                <h3 className="text-lg font-semibold text-white mb-3">Live preview</h3>
                <div className="rounded-lg bg-white/5 border border-white/10">
                  <PageRenderer
                    page={{
                      ...pageDetail,
                      parts: drafts.map((d, i) => ({ id: d.id, kind: d.kind, sortOrder: i, config: d.config })),
                    }}
                  />
                </div>
              </div>
            </>
          ) : null}
        </section>
      )}
    </div>
  );
}

// ---------- Part editor ----------

interface PartEditorProps {
  index: number;
  total: number;
  part: DraftPart;
  pageId: string;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  onChange: (config: PagePartConfig) => void;
}

function PartEditor({ index, total, part, pageId, onMove, onRemove, onChange }: PartEditorProps) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-wide text-white/40">
          {part.kind} part — position {index + 1} of {total}
        </p>
        <div className="flex gap-2">
          <button type="button" disabled={index === 0} onClick={() => onMove(-1)} className="text-xs text-white/70 hover:text-white disabled:opacity-30">↑</button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove(1)} className="text-xs text-white/70 hover:text-white disabled:opacity-30">↓</button>
          <button type="button" onClick={onRemove} className="text-xs text-red-400 hover:text-red-300">Remove</button>
        </div>
      </div>
      {part.kind === 'html' ? (
        <HtmlPartEditor config={part.config as HtmlPartConfig} pageId={pageId} onChange={onChange} />
      ) : (
        <GalleryPartEditor config={part.config as GalleryPartConfig} onChange={onChange} />
      )}
    </div>
  );
}

function HtmlPartEditor({ config, pageId, onChange }: { config: HtmlPartConfig; pageId: string; onChange: (c: PagePartConfig) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs text-white/60 mb-1">Container width</label>
        <select
          value={config.container ?? 'wide'}
          onChange={(e) => onChange({ ...config, container: e.target.value as HtmlPartConfig['container'] })}
          className="input max-w-xs"
        >
          <option value="narrow">narrow</option>
          <option value="wide">wide</option>
          <option value="full">full</option>
        </select>
      </div>
      <RichTextEditor
        label="HTML content"
        value={config.html}
        onChange={(html) => onChange({ ...config, html })}
        minHeight={240}
        pageId={pageId}
      />
    </div>
  );
}

function GalleryPartEditor({ config, onChange }: { config: GalleryPartConfig; onChange: (c: PagePartConfig) => void }) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <div>
        <label className="block text-xs text-white/60 mb-1">Layout</label>
        <select
          value={config.layout ?? 'grid'}
          onChange={(e) => onChange({ ...config, layout: e.target.value as GalleryPartConfig['layout'] })}
          className="input"
        >
          <option value="grid">grid</option>
          <option value="masonry">masonry</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-white/60 mb-1">Folder path (optional)</label>
        <input
          type="text"
          value={config.filter.folderPath ?? ''}
          onChange={(e) => onChange({ ...config, filter: { ...config.filter, folderPath: e.target.value || undefined } })}
          className="input"
          placeholder="Classroom/1996"
        />
      </div>
      <div>
        <label className="block text-xs text-white/60 mb-1">Tags (comma-separated)</label>
        <input
          type="text"
          value={(config.filter.tags ?? []).join(', ')}
          onChange={(e) =>
            onChange({
              ...config,
              filter: {
                ...config.filter,
                tags: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              },
            })
          }
          className="input"
          placeholder="reunion, group"
        />
      </div>
      <div>
        <label className="block text-xs text-white/60 mb-1">Max items</label>
        <input
          type="number"
          min={1}
          max={500}
          value={config.maxItems ?? ''}
          onChange={(e) =>
            onChange({ ...config, maxItems: e.target.value ? Number(e.target.value) : undefined })
          }
          className="input"
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-white/80 md:col-span-2">
        <input
          type="checkbox"
          checked={!!config.showFolderNav}
          onChange={(e) => onChange({ ...config, showFolderNav: e.target.checked })}
        />
        Show folder navigation
      </label>
    </div>
  );
}

export default PageEditor;
